import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllowedTargetTypes,
  getAvailableFrequencies,
  getEntitySelectionType,
  requiresEntitySelection
} from '../../../../../../utils/entityTypeUtils';
import { ChannelOption, CronConfig, FrequencyType, TaskTriggerType } from '../../../../types';
import StrategyMapSelection from '../Selection/StrategyMapSelection';
import WorkUnitSelection from '../Selection/WorkUnitSelection';
import MultiSelect from '../../../../../../components/common/MultiSelect';
import { HiInformationCircle } from 'react-icons/hi';
import { useOrganizationService, useStrategyMapService } from '../../../../../../services';
import { useJwt } from '../../../../../../helpers/common';
import { SMapAction } from '../../../../../../redux/slices/strategyMap';
import useIntegrationStatus from '../../../../../../hooks/useIntegrationStatus';
import {
  addSlackWizardEntity,
  addSlackWizardEntityWithData,
  clearSlackWizardEntities,
  removeSlackWizardEntity,
  setSlackWizardSelectedChannels,
  setSlackWizardSelectedEntities,
  setSlackWizardSelectedEntitiesData,
  setSlackWizardSelectedFrequency,
  setSlackWizardSelectedTaskTriggerTypes,
  setSlackWizardCronConfig,
  setSlackWizardSprintIntegration,
  mergeProjectNamesById,
  mergeRoadmapNamesById,
  mergeStrategyMapNamesById
} from '../../../../../../redux/slices/settings';
import { WizardOption } from '../../../../types';
import SlackUsersSyncBanner from '../SlackUsersSyncBanner';
import { CronScheduler } from '../CronScheduler';

const ConfigPanel: React.FC = () => {
  const dispatch = useDispatch();
  const { isIntegrationActive } = useIntegrationStatus();
  const isJiraEnabled = isIntegrationActive('jira');
  const isAzureEnabled = isIntegrationActive('azure');
  const { fetchLeveldSmap } = useStrategyMapService();
  const { fetchRoadmapsAndProjects, fetchMoreRoadmapsAndProjects, upsertProjectNamesByIds, upsertRoadmapNamesByIds } = useOrganizationService();
  const JWTdata: any = useJwt();
  const org_id = JWTdata?.org_id;

  const {
    slackAvailableNotifications: availableNotifications = [],
    slackChannels = [],
    slackUsers = [],
    slackWizardSelectedEntityType: selectedEntityType = null,
    slackWizardSelectedEntities: selectedEntities = [],
    slackWizardSelectedFrequency: selectedFrequency = 'daily',
    slackWizardSelectedChannels: selectedChannels = [],
    slackWizardSelectedTaskTriggerTypes: selectedTaskTriggers = [],
    slackWizardSelectedNotificationKey: selectedNotificationKey = null,
    slackWizardSprints = [],
    slackWizardSprintIntegration: sprintIntegration = null,
    slackWizardCronConfig: cronConfig = { day: 1, hour: 9, minute: 0, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' },
    projectIdNameMap = {},
    roadmapIdNameMap = {},
    strategyMapIdNameMap = {}
  } = useSelector((state: any) => state.settings);

  const {
    smaps = [],
    loading: strategyMapsLoading = false,
    loadingMore: strategyMapsLoadingMore = false,
    page: strategyMapsPage = 1,
    totalPages: strategyMapsTotalPages = 1,
    searchTerm: strategyMapsSearchTerm = ''
  } = useSelector((state: any) => state.levelMapList);

  const {
    workItems: workItemsData = [],
    workItemsLoading = false,
    workItemsLoadingMore = false,
    workItemsHasMore = false
  } = useSelector((state: any) => state.organization);

  const selectedNotification = useMemo(() => {
    return availableNotifications.find((n: any) => n.key === selectedNotificationKey)
  }, [availableNotifications, selectedNotificationKey]);

  const selectionType = selectedEntityType ? getEntitySelectionType(selectedEntityType) : null;

  // Fetch initial work items data when selection type is work_item
  useEffect(() => {
    if (selectionType === 'work_item') {
      // Fetch initial data with 'all' type
      fetchRoadmapsAndProjects(true, 1, '', 'all');
    }
  }, [selectionType]);

  // Fetch strategy maps when selection type is strategy_map
  useEffect(() => {
    if (selectionType === 'strategy_map') {
      fetchLeveldSmap(1, 50, '', 'smap', null, null);
    }
  }, [selectionType]);

  // Combined labels map for work items (includes both projects and roadmaps)
  const workItemLabelsMap = useMemo(() => {
    return { ...projectIdNameMap, ...roadmapIdNameMap };
  }, [projectIdNameMap, roadmapIdNameMap]);

  const strategyMapOptions = useMemo(() => {
    return (smaps || [])
      .map((smap: any) => ({
        value: String(smap?.id ?? ''),
        label: smap?.name ?? 'Strategy Map',
        isActive: smap?.status ? String(smap.status).toLowerCase() !== 'closed' : true
      }))
      .filter((option: { value: string }) => option.value);
  }, [smaps]);

  const workItemOptions = useMemo(() => {
    return (workItemsData || [])
      .map((item: any) => ({
        value: String(item?.id ?? ''),
        label: item?.name ?? 'Work Item',
        isActive: item?.status ? String(item.status).toLowerCase() !== 'closed' : true,
        type: item?.type // 'roadmap' or 'project' - this becomes entity_type in the payload
      }))
      .filter((option: { value: string }) => option.value);
  }, [workItemsData]);

  // Default sprint integration when both are enabled and none selected
  useEffect(() => {
    if (selectionType !== 'sprint') return;
    if (isJiraEnabled && isAzureEnabled && sprintIntegration === null) {
      dispatch(setSlackWizardSprintIntegration('azure'));
    } else if ((!isJiraEnabled || !isAzureEnabled) && sprintIntegration !== null) {
      dispatch(setSlackWizardSprintIntegration(null));
    }
  }, [selectionType, isJiraEnabled, isAzureEnabled, sprintIntegration, dispatch]);

  const effectiveSprintIntegration = useMemo((): 'jira' | 'azure' | null => {
    if (isJiraEnabled && isAzureEnabled) return sprintIntegration || 'azure';
    if (isAzureEnabled) return 'azure';
    if (isJiraEnabled) return 'jira';
    return null;
  }, [isJiraEnabled, isAzureEnabled, sprintIntegration]);

  // Dropdown options: use sprint.id as value (same as before) so options show. Show all sprints with id and team_name; filter by type when present so Azure/JIRA only see their sprints.
  const sprintTeamOptions = useMemo(() => {
    const wizardSprints = slackWizardSprints || [];
    const filtered =
      effectiveSprintIntegration === 'azure'
        ? wizardSprints.filter((s: any) => s?.id != null && s?.team_name != null && (s?.type === 'azure' || s?.type == null))
        : effectiveSprintIntegration === 'jira'
          ? wizardSprints.filter((s: any) => s?.id != null && s?.team_name != null && (s?.type === 'jira' || s?.type == null))
          : wizardSprints.filter((s: any) => s?.id != null && s?.team_name != null);
    return filtered.map((sprint: any) => {
      const teamLabel = sprint.team_name || 'Team';
      const projectLabel = sprint.project_name || sprint.project || '';
      const label = projectLabel ? `${teamLabel} (${projectLabel})` : teamLabel;
      return { value: String(sprint.id), label };
    });
  }, [slackWizardSprints, effectiveSprintIntegration]);

  const prevSprintIntegrationRef = useRef<'jira' | 'azure' | null>(null);
  useEffect(() => {
    if (selectionType !== 'sprint') return;
    if (prevSprintIntegrationRef.current !== null && prevSprintIntegrationRef.current !== sprintIntegration) {
      dispatch(clearSlackWizardEntities());
    }
    prevSprintIntegrationRef.current = sprintIntegration;
  }, [selectionType, sprintIntegration, dispatch]);

  const mergedChannelOptions: ChannelOption[] = useMemo(() => {
    const map = new Map<string, ChannelOption>();
    slackChannels.forEach((channel: any) => {
      map.set(channel.id, { value: channel.id, label: `#${channel.name}`, type: 'channel' });
    });
    slackUsers.forEach((user: any) => {
      map.set(user.slack_user_id, { value: user.slack_user_id, label: `@${user.name}`, type: 'user' });
    });
    selectedChannels.forEach((opt: ChannelOption) => {
      if (!map.has(opt.value)) {
        map.set(opt.value, opt);
      }
    });
    return Array.from(map.values());
  }, [slackChannels, slackUsers, selectedChannels]);

  const frequencyOptions = useMemo(() => {
    return selectedNotification ? (getAvailableFrequencies(selectedNotification) as FrequencyType[]) : (['daily'] as FrequencyType[]);
  }, [selectedNotification]);

  const allowedTargetTypes = useMemo(() => {
    return selectedNotification ? getAllowedTargetTypes(selectedNotification) : ['channel', 'user'];
  }, [selectedNotification]);

  const allowedTargetSet = useMemo(() => new Set(allowedTargetTypes), [allowedTargetTypes]);

  const availableChannelOptions = useMemo(() => {
    return mergedChannelOptions.filter((option: ChannelOption) => !option.type || allowedTargetSet.has(option.type));
  }, [mergedChannelOptions, allowedTargetSet]);

  // Ensure readable names for selected entities using options -> ids API -> filter
  useEffect(() => {
    if (!selectedEntities || selectedEntities.length === 0) return;
    if (!selectedEntityType) return;

    const type = getEntitySelectionType(selectedEntityType);
    const currentMap = type === 'work_item'
      ? (projectIdNameMap || {})
      : (type === 'strategy_map'
        ? (strategyMapIdNameMap || {})
        : {}
      );
    const options = type === 'work_item'
      ? workItemOptions
      : (type === 'strategy_map'
        ? strategyMapOptions
        : []
      );

    // First: resolve any missing IDs from currently loaded options
    const fromOptions = selectedEntities.reduce((acc: Record<string, string>, id: string) => {
      if (currentMap[id]) return acc;
      const opt = (options || []).find((o: any) => String(o?.value) === String(id));
      if (opt && opt.label) acc[id] = String(opt.label);
      return acc;
    }, {} as Record<string, string>);
    if (type === 'work_item' && Object.keys(fromOptions).length > 0) {
      dispatch(mergeProjectNamesById(fromOptions));
    } else if (type === 'strategy_map' && Object.keys(fromOptions).length > 0) {
      dispatch(mergeStrategyMapNamesById(fromOptions));
    }
    const mapAfterOptions = { ...currentMap, ...fromOptions };

    // Next: fetch any still-missing IDs via IDs API
    const idsToFetch = selectedEntities.filter((id: string) => !mapAfterOptions[id]);
    const finishFilter = (combined: Record<string, string>) => {
      const filtered = selectedEntities.filter((id: string) => Boolean(combined[id]));
      if (filtered.length !== selectedEntities.length) {
        dispatch(setSlackWizardSelectedEntities(filtered));
      }
    };

    if (idsToFetch.length === 0) {
      finishFilter(mapAfterOptions);
      return;
    }

    if (type === 'work_item') {
      if (!org_id) return;
      upsertProjectNamesByIds(org_id, idsToFetch)
        .then((mapped) => {
          const combined = { ...mapAfterOptions, ...mapped };
          finishFilter(combined);
        })
        .catch(() => { finishFilter(mapAfterOptions); });
    } else if (type === 'strategy_map') {
      // No by-IDs API for strategy maps; names come from loaded list only
      finishFilter(mapAfterOptions);
    }
  }, [selectedEntities, selectedEntityType, org_id, workItemOptions, strategyMapOptions, projectIdNameMap, roadmapIdNameMap, strategyMapIdNameMap]);

  const selectedChannelValues = useMemo(
    () => selectedChannels.filter((option: ChannelOption) => !option.type || allowedTargetSet.has(option.type))
      .map((option: ChannelOption) => ({ value: option.value, label: option.label })),
    [selectedChannels, allowedTargetSet]
  );

  const toggleTaskTrigger = useCallback((trigger: TaskTriggerType) => {
    const exists = selectedTaskTriggers.includes(trigger);
    const next = exists ? selectedTaskTriggers.filter((t: TaskTriggerType) => t !== trigger) : [...selectedTaskTriggers, trigger];
    dispatch(setSlackWizardSelectedTaskTriggerTypes(next));
  }, [dispatch, selectedTaskTriggers]);

  const handleChannelChange = useCallback((options: Array<{ value: string; label: string }>) => {
    const mapped = options.map((opt) => mergedChannelOptions.find((m) => m.value === opt.value) ?? { value: opt.value, label: opt.label });
    dispatch(setSlackWizardSelectedChannels(mapped as ChannelOption[]));
  }, [dispatch, mergedChannelOptions]);

  const handleCronChange = useCallback((config: CronConfig) => {
    dispatch(setSlackWizardCronConfig(config));
  }, [dispatch]);

  const sendToLabel = allowedTargetTypes.length === 1
    ? allowedTargetTypes[0] === 'channel'
      ? 'Send to Channels'
      : 'Send to Users'
    : 'Send To Recipients';

  const sendToPlaceholder = allowedTargetTypes.length === 1
    ? allowedTargetTypes[0] === 'channel'
      ? 'Select channels...'
      : 'Select users...'
    : 'Select recipients...';

  const channelHelperText = allowedTargetTypes.length === 1
    ? allowedTargetTypes[0] === 'channel'
      ? 'This notification can only be sent to Slack channels.'
      : 'This notification can only be sent to Slack users.'
    : 'This notification supports both Slack channels and users.';

  const renderSelectionSection = () => {
    if (!selectedEntityType || !requiresEntitySelection(selectedEntityType)) return null;
    if (selectionType === 'strategy_map') {
      return (
        <StrategyMapSelection
          options={strategyMapOptions}
          selectedIds={selectedEntities}
          labelsMap={strategyMapIdNameMap}
          onAdd={(id) => dispatch(addSlackWizardEntity(id))}
          onRemove={(id) => dispatch(removeSlackWizardEntity(id))}
          onClear={() => dispatch(clearSlackWizardEntities())}
          loading={strategyMapsLoading}
          loadingMore={strategyMapsLoadingMore}
          hasMore={strategyMapsPage < strategyMapsTotalPages}
          onLoadMore={() => { fetchLeveldSmap(strategyMapsPage + 1, 50, strategyMapsSearchTerm, 'smap', null, null); }}
          onSearch={(term) => {
            dispatch(SMapAction.setSearchTerm(term));
            fetchLeveldSmap(1, 50, term, 'smap', null, null);
          }}
        />
      );
    }
    if (selectionType === 'work_item') {
      const handleWorkUnitAdd = (option: WizardOption) => {
        // Use 'type' field from backend response ('roadmap' or 'project') as entity_type
        dispatch(addSlackWizardEntityWithData({
          entity_id: option.value,
          type: option.type || 'project'  // 'type' from backend is 'roadmap' or 'project'
        }));
      };
      return (
        <WorkUnitSelection
          options={workItemOptions}
          selectedIds={selectedEntities}
          labelsMap={workItemLabelsMap}
          onAdd={handleWorkUnitAdd}
          onRemove={(id) => dispatch(removeSlackWizardEntity(id))}
          onClear={() => dispatch(clearSlackWizardEntities())}
          loading={workItemsLoading}
          loadingMore={workItemsLoadingMore}
          hasMore={workItemsHasMore}
          onLoadMore={() => { fetchMoreRoadmapsAndProjects(); }}
          onSearch={(term, searchType = 'all') => { fetchRoadmapsAndProjects(true, 1, term, searchType); }}
        />
      );
    }
    if (selectionType === 'sprint') {
      if (!isJiraEnabled && !isAzureEnabled) {
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No sprint integration is enabled. Enable JIRA or Azure DevOps in Settings → Integrations to select teams for sprint notifications.
            </p>
          </div>
        );
      }
      const handleSprintSetSelected = (ids: string[], integration: 'azure' | 'jira') => {
        dispatch(setSlackWizardSelectedEntities(ids));
        const sprints = slackWizardSprints || [];
        const entitiesData = ids.map((id: string) => {
          const sprint = sprints.find((s: any) => String(s?.id) === String(id));
          if (integration === 'azure') {
            return { entity_id: id, project_id: sprint?.project_id ?? '', team_id: sprint?.team_id ?? '' };
          }
          return { entity_id: id, board_id: sprint?.team_id ?? '', project_id: sprint?.project_id ?? '' };
        });
        dispatch(setSlackWizardSelectedEntitiesData(entitiesData as any));
      };
      const selectedValues = selectedEntities.map((id: string) => {
        const opt = sprintTeamOptions.find((o: any) => o.value === id);
        return { value: id, label: opt?.label || id };
      });
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          {isJiraEnabled && isAzureEnabled && (
            <>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Sprint integration
              </label>
              <div className="flex gap-4 mb-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sprint-integration"
                    checked={sprintIntegration === 'azure'}
                    onChange={() => dispatch(setSlackWizardSprintIntegration('azure'))}
                    className="w-4 h-4 text-primary-light focus:ring-primary-light"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Azure DevOps</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sprint-integration"
                    checked={sprintIntegration === 'jira'}
                    onChange={() => dispatch(setSlackWizardSprintIntegration('jira'))}
                    className="w-4 h-4 text-primary-light focus:ring-primary-light"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">JIRA</span>
                </label>
              </div>
            </>
          )}
          <div className="mb-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Choose the teams whose current sprint should trigger notifications
            </p>
            <div className="flex items-start gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-lg">
              <HiInformationCircle className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Sprint notifications will send the <span className="font-semibold text-blue-600 dark:text-blue-400">current sprint report</span> for the selected team/project(s) combination.
              </p>
            </div>
          </div>
          <MultiSelect
            id="sprint-team-multi-select"
            name="teams"
            label="Teams"
            placeholder="Select teams..."
            value={selectedValues}
            onChange={(values) => {
              const ids = values.map((v) => v.value);
              if (effectiveSprintIntegration) handleSprintSetSelected(ids, effectiveSprintIntegration);
            }}
            options={sprintTeamOptions.map((option: any) => ({ value: option.value, label: option.label }))}
            required={true}
            showAsterisk={true}
          />
        </div>
      );
    }
    return null;
  };

  const taskTriggerOptions: Array<{ type: TaskTriggerType; label: string }> = [
    { type: 'assigned', label: 'Task assigned' },
    { type: 'status_changed', label: 'Task status change' },
  ];

  const frequencySection = (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        Frequency
      </label>
      <div className="grid grid-cols-4 gap-3">
        {frequencyOptions.map((freq: FrequencyType) => (
          <button
            key={freq}
            onClick={() => dispatch(setSlackWizardSelectedFrequency(freq))}
            className={`px-4 py-3 rounded-lg border text-sm font-medium capitalize transition-all ${selectedFrequency === freq
              ? 'border-primary-light bg-primary-light text-white shadow-sm'
              : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:border-primary-light dark:hover:border-primary-light hover:shadow-sm'
              }`}
          >
            {freq}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Sync banner always at the top */}
      <SlackUsersSyncBanner />
      {/* Order: For sprint → Frequency first; for others show selection first. No frequency for task. */}
      {selectionType === 'sprint' ? (
        <>
          {frequencySection}
          {/* Cron Scheduler - after frequency for sprint */}
          {selectedFrequency !== 'immediate' && (
            <CronScheduler
              frequency={selectedFrequency as FrequencyType}
              cronConfig={cronConfig}
              onCronChange={handleCronChange}
            />
          )}
          {renderSelectionSection()}
        </>
      ) : (
        <>
          {renderSelectionSection()}
          {selectionType !== 'task' && frequencySection}
          {/* Cron Scheduler - after frequency for non-task types */}
          {selectionType !== 'task' && selectedFrequency !== 'immediate' && (
            <CronScheduler
              frequency={selectedFrequency as FrequencyType}
              cronConfig={cronConfig}
              onCronChange={handleCronChange}
            />
          )}
        </>
      )}

      {selectionType === 'task' && (
        <>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4 shadow-sm">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Notifications will be sent immediately to users assigned to the tasks when the selected trigger events occur.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Trigger Events <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {taskTriggerOptions.map((option) => (
                <label
                  key={option.type}
                  className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedTaskTriggers.includes(option.type)}
                    onChange={() => toggleTaskTrigger(option.type)}
                    className="w-4 h-4 text-primary-light rounded focus:ring-primary-light"
                  />
                  <span className="ml-3 text-sm text-gray-900 dark:text-white">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Hide Send To Recipients for task type - notifications go directly to assigned users */}
      {selectionType !== 'task' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="notification-targets" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {sendToLabel} <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500 dark:text-gray-400">{channelHelperText}</span>
          </div>
          {availableChannelOptions.length > 0 ? (
            <MultiSelect
              id="notification-targets"
              name="notification-targets"
              placeholder={sendToPlaceholder}
              value={selectedChannelValues}
              onChange={handleChannelChange}
              options={availableChannelOptions.map((option) => ({ value: option.value, label: option.label }))}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-600 dark:text-gray-300">
              No eligible Slack recipients available. Add Slack channels or users that match this notification's delivery requirements.
            </div>
          )}
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Missing a channel or user? Use the “Sync users” banner at the top of this wizard to sync Slack and update the recipients list.
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigPanel;